import {
	ALERT_AM1_ASSIGN_NUM_OVER_REQUEST_NUM,
	DivisionRecommenderEnum,
	EXCEL_RESULT_MESSAGES,
	PageTypeEnum,
	UploadExcelError,
} from '@/constant';
import { UploadExcelUserInfo } from '@/services';

import ExcelJS, { Alignment } from 'exceljs';

import { map } from './common.util';
import { calculateMinimumRelaxTime } from './date.util';
import { formatCustomFieldValue } from './excel-upload.util';

export const columnToNumber = (column: string): number => {
	const alphabetLength = 26;
	let number = 0;
	for (let i = 0; i < column.length; i++) {
		number =
			number * alphabetLength +
			(column.charCodeAt(i) - ('A'.charCodeAt(0) - 1));
	}
	return number;
};

export const applyCellStyle = (
	cell: ExcelJS.Cell,
	alignment: Partial<Alignment> = {
		horizontal: 'left',
	}
) => {
	cell.style = {
		border: {
			top: { style: 'thin' },
			left: { style: 'thin' },
			bottom: { style: 'thin' },
			right: { style: 'thin' },
		},
		alignment: {
			vertical: 'middle',
			wrapText: true,
			...alignment,
		},
	};
};

export const applyCellStyleAlignmentCenter = (cell: ExcelJS.Cell) => {
	applyCellStyle(cell, {
		horizontal: 'center',
	});
};

export const getRecommenderTypeLabel = (type: string) => {
	switch (type) {
		case DivisionRecommenderEnum.MARRIAGE_IMMIGRANT:
			return '결혼이민자';
		case DivisionRecommenderEnum.OVERSEAS_LOCAL_GOVERNMENT:
			return '해외 지자체';
		default:
			return type;
	}
};

const generateCommonErrorMessages = (reason: string, fields: string[]) => {
	switch (reason) {
		case UploadExcelError.NOT_NULL:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_2(fields.join(', '));
		case UploadExcelError.INVALID_FORMAT:
		case UploadExcelError.INVALID_LENGTH:
		case UploadExcelError.INVALID_VALUE_LIST:
		case UploadExcelError.NOT_FOUND:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_1(fields.join(', '));
		default:
			return '';
	}
};

const generateErrorMessagesUploadExcelApplicant = ({
	reason,
	fields,
	userInfo,
}: {
	reason: string;
	fields: string[];
	userInfo: UploadExcelUserInfo;
}) => {
	const commonMessage = generateCommonErrorMessages(reason, fields);
	if (commonMessage) return commonMessage;

	switch (reason) {
		case UploadExcelError.EXCEL_AR_1:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AR_1;
		case UploadExcelError.EXCEL_AR_2:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AR_2;
		case UploadExcelError.EXCEL_AR_3:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AR_3;
		case UploadExcelError.AR1_IMPORT_DUPLICATE_EMAIL:
		case UploadExcelError.DUPLICATE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_3;
		case UploadExcelError.EXCEL_AR_4:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AR_4;
		case UploadExcelError.AR1_INVALID_END_DATE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_1(fields.join(', '));
		case UploadExcelError.AR1_CANNOT_CHANGE_PUBLIC_APPLICANT:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AR_5(fields.join(', '));
		case UploadExcelError.EXCEL_AR_ALL_5:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_5_AR(userInfo.districtName);
		case UploadExcelError.EXCEL_AR_ALL_6:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_6_AR;
		case UploadExcelError.EXCEL_AR_ALL_7:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_7;
		case UploadExcelError.EXCEL_AR_ALL_8:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_8;
		case UploadExcelError.EXCEL_AR_6:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AR_6;
		case UploadExcelError.EXCEL_AR_7_1:
		case UploadExcelError.EXCEL_AR_7_2:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AR_7;
		case UploadExcelError.EXCEL_AR_8:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AR_8;
		case UploadExcelError.EXCEL_AR_9_1:
		case UploadExcelError.EXCEL_AR_9_2:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AR_9;
		case UploadExcelError.EXCEL_AR_11:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AR_11;
		case UploadExcelError.EXCEL_AR_12:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AR_12;

		case UploadExcelError.AR1_MID_TERM_DEPARTURE_DATE_IS_NULL:
			return EXCEL_RESULT_MESSAGES.AR1_MID_TERM_DEPARTURE_DATE_IS_NULL;
		case UploadExcelError.AR1_MID_TERM_DEPARTURE_DATE_IS_PAST:
			return EXCEL_RESULT_MESSAGES.AR1_MID_TERM_DEPARTURE_DATE_IS_PAST;

		default:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_1(fields.join(', '));
	}
};

const generateErrorMessagesUploadExcelRecommender = ({
	reason,
	fields,
}: {
	reason: string;
	fields: string[];
}) => {
	const commonMessage = generateCommonErrorMessages(reason, fields);
	if (commonMessage) return commonMessage;

	switch (reason) {
		case UploadExcelError.RM1_TYPE_NOT_CORRECT:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_RM_1;
		case UploadExcelError.RM1_SPOUSE_NAME_NOT_EXIST:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_RM_2;
		default:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_1(fields.join(', '));
	}
};

const generateErrorMessagesUploadExcelLodging = ({
	reason,
	fields,
	userInfo,
}: {
	reason: string;
	fields: string[];
	userInfo: UploadExcelUserInfo;
}) => {
	const commonMessage = generateCommonErrorMessages(reason, fields);
	if (commonMessage) return commonMessage;

	switch (reason) {
		case UploadExcelError.DUPLICATE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_LM_1;
		case UploadExcelError.EXCEL_LM_2:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_LM_2;
		case UploadExcelError.EXCEL_LM_3:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_LM_3;
		case UploadExcelError.EXCEL_LM_4:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_LM_4(
				userInfo?.districtName || ''
			);
		case UploadExcelError.EXCEL_LM_5:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_LM_5;
		case UploadExcelError.EXCEL_LM_6_FRANCHISE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_LM_6_FRANCHISE;
		case UploadExcelError.EXCEL_LM_6_RECOMMENDER:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_LM_6_RECOMMENDER;
		default:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_1(fields.join(', '));
	}
};

const generateErrorMessagesUploadExcelFranchise = ({
	reason,
	fields,
	userInfo,
}: {
	reason: string;
	fields: string[];
	userInfo: UploadExcelUserInfo;
}) => {
	const commonMessage = generateCommonErrorMessages(reason, fields);
	if (commonMessage) return commonMessage;

	switch (reason) {
		case UploadExcelError.CAN_NOT_INPUT_PUBLIC_FRANCHISE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_FR_1;
		case UploadExcelError.FR1_PUBLIC_MAIN_FRANCHISE_NOT_FOUND:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_FR_2;
		case UploadExcelError.FR1_INVALID_GRADE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_FR_3;
		case UploadExcelError.FR1_DUPLICATE_BUSINESS_CORPORATE_REGIST_NO:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_FR_4;
		case UploadExcelError.EXCEL_FR_5:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_FR_5;
		case UploadExcelError.EXCEL_FR_6:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_FR_6;
		case UploadExcelError.EXCEL_FR_7:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_FR_7(fields.join(', '));
		case UploadExcelError.EXCEL_FR_8:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_FR_8;
		case UploadExcelError.DUPLICATE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_3;
		case UploadExcelError.EXCEL_FR_ALL_5:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_5_FR(userInfo.districtName);
		case UploadExcelError.EXCEL_FR_ALL_6:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_6_FR;
		case UploadExcelError.EXCEL_FR_ALL_7:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_7;
		case UploadExcelError.EXCEL_FR_ALL_8:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_8;
		default:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_1(fields.join(', '));
	}
};

const generateErrorMessagesUploadExcelAssignment = ({
	reason,
	fields,
}: {
	reason: string;
	fields: string[];
}) => {
	const commonMessage = generateCommonErrorMessages(reason, fields);
	if (commonMessage) return commonMessage;

	switch (reason) {
		case UploadExcelError.EXCEL_AM_1:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AM_1;
		case UploadExcelError.AM1_INVALID_APPLICANT_EMAIL:
		case UploadExcelError.AM1_INVALID_APPLICANT_FIRST_NAME:
		case UploadExcelError.AM1_INVALID_APPLICANT_LAST_NAME:
		case UploadExcelError.AM1_INVALID_APPLICANT_BIRTH_DATE:
		case UploadExcelError.AM1_PUBLIC_INVALID_APPLICANT:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AM_2;
		case UploadExcelError.AM1_INVALID_FRANCHISE_EMAIL:
		case UploadExcelError.AM1_INVALID_FRANCHISE_NAME:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AM_3;
		case UploadExcelError.EXCEL_AM_4:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AM_4;
		case UploadExcelError.AM1_CANNOT_ASSIGN_PUBLIC_APPLICANT:
		case UploadExcelError.AM1_INVALID_WORKING_DATE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AM_5;
		case UploadExcelError.EXCEL_AM_6:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AM_6;
		case UploadExcelError.AM1_INVALID_DISTRICT_ALERT_63:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AM_AM1_INVALID_DISTRICT_ALERT_63;
		default:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_1(fields.join(', '));
	}
};

const generateErrorMessagesUploadExcelPublicAssignment = ({
	reason,
	fields,
	data,
}: {
	reason: string;
	fields: string[];
	data: any;
}) => {
	const commonMessage = generateCommonErrorMessages(reason, fields);
	if (commonMessage) return commonMessage;

	switch (reason) {
		case UploadExcelError.AM1_PUBLIC_INVALID_FRANCHISE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AM_3;
		case UploadExcelError.AM1_PUBLIC_INVALID_REQUEST_APPLICANT:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_PAM_1;
		case UploadExcelError.AM1_PUBLIC_DUPLICATE_APPLICANT_OR_FRANCHISE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_PAM_5(fields.join(', '));
		case UploadExcelError.AM1_PUBLIC_NOT_FOUND_APPLICANT:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_PAM_3;
		case UploadExcelError.AM1_PUBLIC_APPLICANT_OR_FRANCHISE_BIRTHDAY_NULL:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_PAM_4(fields.join(', '));
		case UploadExcelError.AM1_PUBLIC_INVALID_REQUEST_APPLICANT_MANUAL:
			return ALERT_AM1_ASSIGN_NUM_OVER_REQUEST_NUM(data?.franchiseName || '');
		case UploadExcelError.AM1_PUBLIC_INVALID_RELAX_TIME:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_PAM_6(
				calculateMinimumRelaxTime(data?.startTime, data?.endTime) || 0
			);
		case UploadExcelError.AM1_PUBLIC_NOT_ENOUGH_MALE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_PAM_2_MALE(
				data?.numberOfMale || 0
			);
		case UploadExcelError.AM1_PUBLIC_NOT_ENOUGH_FEMALE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_PAM_2_FEMALE(
				data?.numberOfFemale || 0
			);
		case UploadExcelError.AM1_PUBLIC_NOT_ENOUGH_FEMALE_AND_MALE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_PAM_2_FEMALE_AND_MALE(
				Number(data?.numberOfMale || 0) + Number(data?.numberOfFemale || 0)
			);
		case UploadExcelError.AM1_INVALID_WORKING_DATE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AM_5;
		case UploadExcelError.AM1_PUBLIC_INVALID_APPLICANT:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_AM_2;
		case UploadExcelError.AM1_PUBLIC_EXIST_ASSIGNMENT:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_PAM_7;
		case UploadExcelError.AM1_PUBLIC_APPLICANT_FRANCHISE_NOT_MATCH_PUBLIC_FRANCHISE:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_PAM_8;
		default:
			return EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_1(fields.join(', '));
	}
};

export const generateErrorMessagesUploadExcel = ({
	response,
	templateFields,
	pageType,
	userInfo,
}: {
	response: {
		data?: any;
		errorList: Array<{
			field: string;
			reason: string;
		}>;
	};
	templateFields: Array<{ label: string; value: string }>;
	pageType: PageTypeEnum;
	userInfo?: UploadExcelUserInfo;
}) => {
	const { errorList, data } = response;
	const filteredErrorList = errorList.filter((error) => error.reason); // remove error with reason is empty
	//normalize error reasons to standard format
	const normalizedErrorList = filteredErrorList.map((error) => {
		if (
			error.reason === UploadExcelError.INVALID_LENGTH ||
			error.reason === UploadExcelError.INVALID_VALUE_LIST ||
			error.reason === UploadExcelError.NOT_FOUND
		) {
			return {
				...error,
				reason: UploadExcelError.INVALID_FORMAT,
			};
		}
		return error;
	});
	// First group errors by field
	const fieldGroups = normalizedErrorList.reduce(
		(acc, error) => {
			if (!acc[error.field]) {
				acc[error.field] = [];
			}
			acc[error.field].push(error);
			return acc;
		},
		{} as Record<string, typeof filteredErrorList>
	);

	// Then process each field group
	const groupErrorList = Object.entries(fieldGroups).flatMap(
		([field, errors]) => {
			// Separate format-related errors from other errors
			const formatErrors = errors.filter((error) =>
				[
					UploadExcelError.INVALID_FORMAT,
					UploadExcelError.INVALID_LENGTH,
					UploadExcelError.INVALID_VALUE_LIST,
					UploadExcelError.NOT_FOUND,
				].includes(error.reason as UploadExcelError)
			);
			const otherErrors = errors.filter(
				(error) =>
					![
						UploadExcelError.INVALID_FORMAT,
						UploadExcelError.INVALID_LENGTH,
						UploadExcelError.INVALID_VALUE_LIST,
						UploadExcelError.NOT_FOUND,
					].includes(error.reason as UploadExcelError)
			);

			// If there are multiple format errors, group them into one
			const groupedFormatError =
				formatErrors.length > 1
					? [{ field, reason: UploadExcelError.INVALID_FORMAT }]
					: formatErrors;

			// Return both grouped format errors and other errors
			return [...groupedFormatError, ...otherErrors];
		}
	);

	// sort errorList by order in templateFields
	const sortedErrorList = groupErrorList.sort((a, b) => {
		const indexA = templateFields.findIndex((field) => field.value === a.field);
		const indexB = templateFields.findIndex((field) => field.value === b.field);
		return indexA - indexB;
	});

	const templateFieldsWithRow = templateFields.concat({
		label: 'row',
		value: 'row',
	});

	// group error by reason
	const groupedErrors = sortedErrorList.reduce(
		(acc, error) => {
			const { field, reason } = error;

			// find label corresponding to field in templateFields
			const templateField = templateFieldsWithRow.find(
				(template) => template.value === field
			);

			if (templateField) {
				// if error reason already exists in acc, add new field to list
				if (acc[reason]) {
					acc[reason].push(templateField.label);
				} else {
					acc[reason] = [templateField.label];
				}
			}
			return acc;
		},
		{} as Record<string, string[]>
	);

	// create list of error messages
	const errorMessages = Object.entries(groupedErrors).map(
		([reason, fields]) => {
			switch (pageType) {
				case PageTypeEnum.APPLICANT:
					return generateErrorMessagesUploadExcelApplicant({
						reason,
						fields,
						userInfo: userInfo ?? ({} as UploadExcelUserInfo),
					});
				case PageTypeEnum.RECOMMENDER:
					return generateErrorMessagesUploadExcelRecommender({
						reason,
						fields,
					});
				case PageTypeEnum.LODGING:
					return generateErrorMessagesUploadExcelLodging({
						reason,
						fields,
						userInfo: userInfo ?? ({} as UploadExcelUserInfo),
					});
				case PageTypeEnum.FRANCHISE:
					return generateErrorMessagesUploadExcelFranchise({
						reason,
						fields,
						userInfo: userInfo ?? ({} as UploadExcelUserInfo),
					});
				case PageTypeEnum.ASSIGNMENT:
					return generateErrorMessagesUploadExcelAssignment({
						reason,
						fields,
					});
				case PageTypeEnum.PUBLIC_ASSIGNMENT:
					return generateErrorMessagesUploadExcelPublicAssignment({
						reason,
						fields: map(fields, (field) => field.replaceAll('_', ':')),
						data,
					});
				default:
					return generateCommonErrorMessages(reason, fields);
			}
		}
	);

	return errorMessages.join(' | ');
};

export const formatCustomFieldValueToExcel = (fields: Record<string, any>) => {
	const result = {} as Record<string, any>;
	Object.entries(fields).forEach(([key, value]) => {
		if (value !== null && value !== undefined) {
			result[key] = formatCustomFieldValue(value);
		}
	});
	return result;
};
